import { useCallback, useEffect, useState } from "react";
import { Download, FileText, Loader2, Plus, Trash2, Truck } from "lucide-react";
import { API, api, apiError, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListState, PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DocumentJourney } from "@/components/DocumentJourney";
import {
  ConfirmDispatchButton, DispatchPackButton, EwayStatusDialog,
} from "@/components/DispatchActions";
import { DispatchProofDialog } from "@/components/DispatchProof";
import { EwayButton } from "@/components/EwayGenerate";

const nextUid = () => `line-${Math.random().toString(36).slice(2, 10)}`;
const BLANK_FORM = {
  customer_id: "", project_name: "", sales_order_id: "", sales_order_number: "",
  delivery_address: "", dispatch_date: new Date().toISOString().slice(0, 10),
  vehicle_number: "", vehicle_type: "Truck", driver_name: "", driver_mobile: "",
  transporter: "", transporter_gstin: "", distance_km: "", lr_number: "", eway_bill: "",
  notes: "", items: [],
};

function challanVariant(status) {
  if (status === "Dispatched") return "default";
  if (status === "Cancelled") return "destructive";
  return "outline";
}

export default function Dispatch() {
  const { can } = useAuth();
  const [challans, setChallans] = useState([]);
  const [stock, setStock] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [ewayWarning, setEwayWarning] = useState(null);
  const [eway, setEway] = useState(null);
  const [ewayStatus, setEwayStatus] = useState(null);
  const [journey, setJourney] = useState(null);
  const [proof, setProof] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s, cu, o] = await Promise.all([
        api.get("/challans"),
        api.get("/stock"),
        api.get("/customers?limit=200"),
        api.get("/sales-orders").catch(() => ({ data: [] })),
      ]);
      setChallans(c.data);
      setStock(s.data.items.filter((i) => i.item_type === "finished_good"));
      setCustomers(cu.data.items);
      setOrders(o.data);
    } catch (e) {
      logError("dispatch", e);
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  const save = async () => {
    try {
      if (!form.customer_id) return toast.error("Select the customer");
      const items = form.items
        .filter((i) => i.stock_item_id)
        .map(({ uid, ...i }) => ({ ...i, quantity: Number(i.quantity || 0) }));
      if (!items.length) return toast.error("Add at least one finished product to dispatch");
      const so = orders.find((o) => o.id === form.sales_order_id);
      await api.post("/challans", {
        ...form, items, sales_order_number: so?.number || "",
      });
      toast.success("Delivery challan created");
      setOpen(false);
      setForm(BLANK_FORM);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const dispatchNow = async (c, acknowledged = false) => {
    if (!acknowledged && !window.confirm(`Dispatch ${c.number}? Finished goods stock will be reduced.`)) return;
    setBusy(c.id);
    try {
      await api.post(`/challans/${c.id}/dispatch`, { eway_acknowledged: acknowledged });
      toast.success("Dispatched — stock updated");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "eway_missing") {
        setEwayWarning({ challan: c, ...detail });
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setBusy("");
    }
  };

  const openEway = async (c) => {
    setBusy(`eway-${c.id}`);
    try {
      const { data } = await api.get(`/challans/${c.id}/eway`);
      setEway({ ...data, challan_id: c.id });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const copyEway = async () => {
    const d = eway;
    const text = [
      `Document: ${d.document_type} ${d.document_number} dated ${d.document_date}`,
      `From: ${d.consignor.name} • GSTIN ${d.consignor.gstin} • ${d.consignor.place} ${d.consignor.pincode} • ${d.consignor.state} (${d.consignor.state_code})`,
      `To: ${d.consignee.name} • GSTIN ${d.consignee.gstin} • ${d.consignee.address} • ${d.consignee.place} ${d.consignee.pincode} • ${d.consignee.state} (${d.consignee.state_code})`,
      `Place of supply: ${d.place_of_supply}`,
      `Taxable ₹${d.taxable_value} • CGST ₹${d.cgst_value} • SGST ₹${d.sgst_value} • IGST ₹${d.igst_value} • Total ₹${d.total_invoice_value}`,
      `Transport: ${d.transport_mode} • ${d.transporter_name || "-"} • Vehicle ${d.vehicle_number || "-"} • LR ${d.lr_number || "-"} • Distance ${d.distance_km || "-"} km`,
      "Items:",
      ...d.items.map((i) => `  ${i.product_name} • HSN ${i.hsn_sac || "-"} • ${i.quantity} ${i.unit} • ₹${i.taxable_value} • GST ${i.gst_rate}%`),
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("E-way details copied — paste them into the portal");
    } catch {
      toast.info("Copy blocked by the browser — use the JSON or CSV download");
    }
  };

  return (
    <div>
      <PageHeader
        testId="dispatch-header"
        title="Dispatch & Delivery Challans"
        subtitle={`${challans.length} challans • ${challans.filter((c) => c.status === "Dispatched").length} dispatched`}
        actions={
          can("dispatch", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-challan-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Challan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                  <DialogTitle>New delivery challan</DialogTitle>
                  <DialogDescription>
                    Pick finished goods, add vehicle and driver details, then dispatch to reduce stock.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Field label="Customer">
                    <Select
                      value={form.customer_id}
                      onValueChange={(v) => {
                        const c = customers.find((x) => x.id === v);
                        setForm({ ...form, customer_id: v, delivery_address: c?.shipping_address || "" });
                      }}
                    >
                      <SelectTrigger data-testid="challan-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Sales order (optional)">
                    <Select
                      value={form.sales_order_id}
                      onValueChange={(v) => {
                        const o = orders.find((x) => x.id === v);
                        setForm({
                          ...form, sales_order_id: v,
                          customer_id: o?.customer_id || form.customer_id,
                          project_name: o?.project_name || form.project_name,
                        });
                      }}
                    >
                      <SelectTrigger data-testid="challan-order-select">
                        <SelectValue placeholder="Link an order" />
                      </SelectTrigger>
                      <SelectContent>
                        {orders.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.number} — {o.customer_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Dispatch date">
                    <Input
                      type="date"
                      data-testid="challan-date-input"
                      value={form.dispatch_date}
                      onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })}
                    />
                  </Field>
                  <Field label="Project" className="sm:col-span-1">
                    <Input
                      data-testid="challan-project-input"
                      value={form.project_name}
                      onChange={(e) => setForm({ ...form, project_name: e.target.value })}
                    />
                  </Field>
                  <Field label="Delivery address" className="sm:col-span-2">
                    <Textarea
                      data-testid="challan-address-input"
                      value={form.delivery_address}
                      onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                    />
                  </Field>
                </div>

                <div className="overline mt-2">Goods being dispatched</div>
                {form.items.map((it, i) => (
                  <div key={it.uid} className="grid grid-cols-2 items-end gap-2 sm:grid-cols-6">
                    <Field label="Finished product" className="col-span-2 sm:col-span-3">
                      <Select
                        value={it.stock_item_id}
                        onValueChange={(v) => {
                          const s = stock.find((x) => x.id === v);
                          setItem(i, { stock_item_id: v, name: s?.name || "", unit: s?.unit || "Nos" });
                        }}
                      >
                        <SelectTrigger data-testid={`challan-item-select-${i}`}>
                          <SelectValue placeholder="Select from finished goods" />
                        </SelectTrigger>
                        <SelectContent>
                          {stock.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name} ({s.quantity} {s.unit} in {s.warehouse_name})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Quantity">
                      <Input
                        data-testid={`challan-item-qty-${i}`}
                        value={it.quantity}
                        onChange={(e) => setItem(i, { quantity: e.target.value })}
                      />
                    </Field>
                    <Field label="Remarks" className="sm:col-span-1">
                      <Input value={it.remarks} onChange={(e) => setItem(i, { remarks: e.target.value })} />
                    </Field>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`challan-item-remove-${i}`}
                      onClick={() => setForm({ ...form, items: form.items.filter((x) => x.uid !== it.uid) })}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="challan-add-item-button"
                  onClick={() =>
                    setForm({
                      ...form,
                      items: [...form.items, { uid: nextUid(), stock_item_id: "", name: "", unit: "Nos", quantity: 1, remarks: "" }],
                    })
                  }
                >
                  Add product
                </Button>
                {stock.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No finished goods in stock yet — complete a production order first.
                  </p>
                )}

                <div className="overline mt-2">Vehicle & driver</div>
                <div className="grid gap-3 sm:grid-cols-3">
                  {[["vehicle_number", "Vehicle number", "challan-vehicle-input"],
                    ["vehicle_type", "Vehicle type", null],
                    ["driver_name", "Driver name", "challan-driver-input"],
                    ["driver_mobile", "Driver mobile", "challan-driver-mobile-input"],
                    ["transporter", "Transporter", null],
                    ["transporter_gstin", "Transporter GSTIN / ID", null],
                    ["distance_km", "Approx distance (km)", "challan-distance-input"],
                    ["lr_number", "LR / docket number", null],
                    ["eway_bill", "E-way bill number", "challan-eway-input"]].map(([k, l, tid]) => (
                    <Field key={k} label={l}>
                      <Input
                        data-testid={tid || undefined}
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label="Notes" className="sm:col-span-2">
                    <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                  </Field>
                </div>
                <DialogFooter>
                  <Button data-testid="challan-save-button" onClick={save}>
                    Create challan
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="p-4 sm:p-6">
        {journey && (
          <div className="mb-3">
            <DocumentJourney entity="challan" id={journey} />
          </div>
        )}
        <ListState loading={loading} isEmpty={challans.length === 0}
          emptyTitle="No delivery challans"
          emptyHint="Create a challan to dispatch finished goods — stock reduces on dispatch.">
            <div className="surface overflow-x-auto" data-testid="challans-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Challan</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden md:table-cell">Vehicle / driver</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {challans.map((c) => (
                    <TableRow key={c.id} data-testid={`challan-row-${c.id}`}>
                      <TableCell className="font-mono-num text-xs">
                        {c.number}
                        <div className="text-[10px] text-muted-foreground">{shortDate(c.dispatch_date)}</div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.customer_name}
                        <div className="text-[10px] text-muted-foreground">{c.project_name || "—"}</div>
                      </TableCell>
                      <TableCell className="hidden text-xs md:table-cell">
                        {c.vehicle_number || "—"}
                        <div className="text-[10px] text-muted-foreground">
                          {c.driver_name || "—"} {c.driver_mobile ? `• ${c.driver_mobile}` : ""}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">
                        {(c.items || []).length}
                      </TableCell>
                      <TableCell>
                        <Badge variant={challanVariant(c.status)} className="text-[10px]">{c.status}</Badge>
                        <Badge variant="outline" className="ml-1 text-[9px]"
                          data-testid={`eway-state-badge-${c.id}`}>
                          e-way: {c.eway_status || (c.eway_bill ? "Generated" : "Draft")}
                        </Badge>
                        {c.part_number > 1 && (
                          <Badge variant="secondary" className="ml-1 text-[9px]"
                            data-testid={`challan-part-badge-${c.id}`}>
                            part {c.part_number}
                          </Badge>
                        )}
                        {c.eway_waived && (
                          <Badge variant="outline" className="ml-1 text-[9px]" data-testid={`eway-waived-badge-${c.id}`}>
                            no e-way bill
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="space-x-1 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          data-testid={`challan-pdf-button-${c.id}`}
                          onClick={() => window.open(`${API}/challans/${c.id}/pdf`, "_blank")}
                        >
                          <Download className="h-3.5 w-3.5" /> PDF
                        </Button>
                        <DispatchPackButton challan={c} />
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          data-testid={`challan-proof-button-${c.id}`}
                          onClick={() => setProof(c)}
                        >
                          Photos
                          {(c.proof_photos || []).length > 0 && ` (${c.proof_photos.length})`}
                        </Button>
                        <EwayButton challan={c} onDone={load} />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1"
                          data-testid={`challan-eway-sheet-button-${c.id}`}
                          onClick={() => openEway(c)}
                        >
                          Sheet
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1"
                          data-testid={`challan-journey-button-${c.id}`}
                          onClick={() => setJourney(journey === c.id ? null : c.id)}
                        >
                          Journey
                        </Button>
                        {["Draft", "Ready"].includes(c.status) && can("dispatch", "edit") && (
                          <ConfirmDispatchButton challan={c} onDone={load} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
        </ListState>
      </div>
      <Dialog open={!!ewayWarning} onOpenChange={(o) => !o && setEwayWarning(null)}>        <DialogContent data-testid="eway-warning-dialog">          <DialogHeader>
            <DialogTitle>E-way bill missing</DialogTitle>
            <DialogDescription>
              GST rules require an e-way bill for high-value consignments.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm" data-testid="eway-warning-message">{ewayWarning?.message}</p>
          <div className="rounded border border-border bg-secondary p-3 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Consignment value</span>
              <span className="font-mono-num">
                ₹{Number(ewayWarning?.consignment_value || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">E-way bill limit</span>
              <span className="font-mono-num">
                ₹{Number(ewayWarning?.threshold || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="eway-add-number-button"
              onClick={() => {
                setEwayWarning(null);
                toast.info("Open the challan, add the e-way bill number, then dispatch again");
              }}
            >
              I'll add the e-way bill
            </Button>
            <Button
              variant="destructive"
              data-testid="eway-dispatch-anyway-button"
              onClick={() => {
                const c = ewayWarning.challan;
                setEwayWarning(null);
                dispatchNow(c, true);
              }}
            >
              Dispatch without e-way bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!eway} onOpenChange={(o) => !o && setEway(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
          data-testid="eway-details-dialog">
          <DialogHeader>
            <DialogTitle>E-way bill details — {eway?.challan_number}</DialogTitle>
            <DialogDescription>
              Filled in from the challan and its invoice. Copy them into the government portal,
              or download the bulk-upload file.
            </DialogDescription>
          </DialogHeader>
          {eway && (
            <div className="space-y-3 text-xs">
              {eway.missing?.length > 0 && (
                <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2.5"
                  data-testid="eway-missing-list">
                  Still needed before upload: {eway.missing.join(", ")}
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {[["Consignor (us)", eway.consignor], ["Consignee (customer)", eway.consignee]]
                  .map(([title, p]) => (
                    <div key={title} className="surface p-3">
                      <div className="overline">{title}</div>
                      <div className="mt-1 space-y-0.5">
                        <div className="font-medium">{p.name}</div>
                        <div>GSTIN {p.gstin || "—"}</div>
                        <div>{p.address || "—"}</div>
                        <div>{p.place} {p.pincode}</div>
                        <div>{p.state} ({p.state_code})</div>
                      </div>
                    </div>
                  ))}
              </div>
              <div className="surface grid gap-2 p-3 sm:grid-cols-3" data-testid="eway-summary">
                {[
                  ["Document", `${eway.document_type} ${eway.document_number}`],
                  ["Document date", eway.document_date],
                  ["Place of supply", eway.place_of_supply],
                  ["Taxable value", `₹${Number(eway.taxable_value).toLocaleString("en-IN")}`],
                  ["CGST / SGST", `₹${eway.cgst_value} / ₹${eway.sgst_value}`],
                  ["IGST", `₹${eway.igst_value}`],
                  ["Total invoice value", `₹${Number(eway.total_invoice_value).toLocaleString("en-IN")}`],
                  ["Vehicle", eway.vehicle_number || "—"],
                  ["Distance (km)", eway.distance_km || "—"],
                  ["Transporter", eway.transporter_name || "—"],
                  ["LR / docket", eway.lr_number || "—"],
                  ["E-way bill no.", eway.eway_bill || "not generated yet"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="overline">{k}</div>
                    <div>{v}</div>
                  </div>
                ))}
              </div>
              <div className="surface overflow-x-auto" data-testid="eway-items-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Taxable</TableHead>
                      <TableHead className="text-right">GST %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eway.items.map((i, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs">{i.product_name}</TableCell>
                        <TableCell className="text-xs">{i.hsn_sac || "—"}</TableCell>
                        <TableCell className="text-right text-xs">{i.quantity}</TableCell>
                        <TableCell className="text-xs">{i.unit}</TableCell>
                        <TableCell className="text-right text-xs">{i.taxable_value}</TableCell>
                        <TableCell className="text-right text-xs">{i.gst_rate}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" data-testid="eway-copy-button" onClick={copyEway}>
              Copy e-way details
            </Button>
            <Button variant="outline" data-testid="eway-json-button"
              onClick={() => window.open(`${API}/challans/${eway.challan_id}/eway.json`, "_blank")}>
              <Download className="mr-1 h-3.5 w-3.5" /> Portal JSON
            </Button>
            <Button variant="outline" data-testid="eway-csv-button"
              onClick={() => window.open(`${API}/challans/${eway.challan_id}/eway.csv`, "_blank")}>
              <Download className="mr-1 h-3.5 w-3.5" /> Excel / CSV
            </Button>
            <Button data-testid="eway-print-button" onClick={() => window.print()}>
              Print sheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ewayStatus && (        <EwayStatusDialog challan={ewayStatus} onClose={() => setEwayStatus(null)}
          onSaved={load} />
      )}
      {proof && (
        <DispatchProofDialog challan={proof} onClose={() => setProof(null)} onSaved={load} />
      )}
    </div>
  );
}
